import { CollapseSection, Drawer } from '@/components/ats';
import { FormBuilder } from '@/components/formBuilder';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { useShowToast } from '@/hooks';
import {
  useCreateScheduleReportMutation,
  // todo: enable if api call needed
  // useLazyGetScheduleReportByIdQuery,
  useUpdateScheduleReportMutation,
} from '@/store/query/ats-reports/scheduleReportsEmail';
import { selectUsers } from '@/store/users/selectors';
import { Avatar, Box, Flex, Text } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectedFilters } from '../filter/selected';
import * as Yup from 'yup';
import KTabBar from '@/ui-library/components/atoms/KTabBar/KTabBar';
import { ScheduleSettingsTabs } from './schedule-reports-settings';
import { editMe } from '@/store/me/actions';
import { getDefaultTimeZone } from '@/components/formBuilder/formFields/DateField/helpers';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { KText } from '@/ui-library/components/text';

const collapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    margin: '0 -24px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'white',
  },
});

export const CreateEditScheduleReportsEmail = ({
  selectedReport = undefined,
  modalActions,
  currentData,
  setCurrentView,
  isEmptySchedules,
  isCustomReport = false,
}) => {
  const formRef = useRef(null);
  const users = useSelector(selectUsers);
  const [initialValues, setInitialValues] = useState({});
  const modalContainerRef = useRef(null);

  const isEditMode = selectedReport?.id;
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrUser);
  const { time_zone, feature_options = {} } = currentUser || {};
  const IsAgencyEnabled = feature_options?.is_agency ?? false;

  useEffect(() => {
    dispatch(editMe({}));
    if (selectedReport?.id) {
      const {
        name,
        description,
        recipients,
        external_recipient_emails,
        frequency,
      } = selectedReport || {};
      setInitialValues({
        name,
        description,
        frequency,
        recipient_ids: [
          ...(recipients ?? []).map((i) => i?.id),
          ...(external_recipient_emails ?? []),
        ],
        timezone:
          selectedReport?.timezone || getDefaultTimeZone(time_zone)?.key,
        deliver_time: selectedReport?.deliver_time,
        deliver_day_of_week: selectedReport?.deliver_day_of_week,
        deliver_day_of_month: selectedReport?.deliver_day_of_month,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, dispatch, selectedReport]);

  // todo: enable if api call needed
  // const [getCurrentReport, { data, isFetching }] =
  //   useLazyGetScheduleReportByIdQuery();

  // useEffect(() => {
  //   if (isEditMode) {
  //     getCurrentReport({ id: selectedReport?.id });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedReport]);

  const [createReport, { isLoading: isCreating }] =
    useCreateScheduleReportMutation();
  const [updateReport, { isLoading: isUpdating }] =
    useUpdateScheduleReportMutation();
  const [selectedTab, setSelectedTab] = useState(
    selectedReport?.frequency || 'daily'
  );
  const emailRegex =
    // eslint-disable-next-line no-useless-escape
    /^[a-zA-Z0-9.!\#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  const options = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
  ];

  const toast = useShowToast();

  const {
    filters,
    group,
    timeFrame,
    subject_type,
    selected,
    passThroughMetric,
  } = currentData || {};
  const formConfig = [
    {
      label: 'Report name',
      type: 'input',
      name: 'name',
      maxLength: 250,
      isRequired: true,
    },
    {
      label: 'Description',
      type: 'textArea',
      name: 'description',
      maxLength: 10000,
      validation: Yup.string()
        .trim()
        .max(10000, 'Max 10000 characters allowed'),
    },
    {
      type: 'custom',
      Field: () => {
        return (
          <Flex
            flexDir="column"
            gap="10px"
            bg="secondary-25"
            p="16px"
            borderRadius="sm"
          >
            <Text fontSize="sm">
              <Text as="span" size="sm-medium">
                Filters:{' '}
              </Text>
              The report will be emailed with your current filters applied
            </Text>
            {(
              isEditMode
                ? selectedReport?.metadata?.filtersToRender?.length > 0
                : selected?.length > 0
            ) ? (
              <SelectedFilters
                selected={
                  isEditMode
                    ? selectedReport?.metadata?.filtersToRender || []
                    : selected
                }
                onRemoveFilter={() => {}}
                disableClearAll={true}
                showValuesOnTooltip={true}
                styles={{
                  bg: 'white',
                  borderRadius: '4px',
                  border: '1px solid #E9EBF4',
                }}
              />
            ) : (
              <Text fontSize="sm">No filters applied</Text>
            )}
          </Flex>
        );
      },
    },
    {
      type: 'asyncSelectWithCreate',
      isMulti: true,
      options: [
        ...(users ?? []).map((user) => ({
          label: `${user?.name}`,
          value: user?.id,
          avatar: user?.avatar,
          ...user,
        })),
        ...(selectedReport?.external_recipient_emails || []).map((email) => ({
          label: email,
          value: email,
          customText: 'External',
        })),
      ],
      valueKey: 'value',
      labelKey: 'label',
      validationType: 'array',
      isRequired: true,
      useInput: true,
      name: 'recipient_ids',
      label: 'Add recipient',
      placeholder: 'Search recipients',
      withEmail: true,
      loadDefaultOptions: false,
      customFormatLabel: SelectOptionWithAvatar,
      customLabelSelect: (val) => {
        if (!val) return null;

        const isValidEmail = emailRegex.test(val) && IsAgencyEnabled;
        if (!isValidEmail) {
          return null;
        }
        return (
          <Flex alignItems="center" gap="6px">
            <Avatar
              name={val}
              width="16px"
              height="16px"
              borderRadius="3px"
              className="avatar-xs"
            />
            <KText>{val}</KText>
            <KText size="sm-semi-bold">(External)</KText>
          </Flex>
        );
      },
      customOnCreate: (inputValue) => {
        return {
          label: inputValue,
          value: inputValue,
          customText: 'External',
        };
      },
      validateNewOption: (inputValue) => {
        const isValidEmail = emailRegex.test(inputValue) && IsAgencyEnabled;
        if (!isValidEmail) {
          return false;
        }
        return true;
      },
      customNoOptionsMessage: (props) => {
        const searchTerm = props?.selectProps?.inputValue;
        if (searchTerm && searchTerm.length > 0) {
          if (!emailRegex.test(searchTerm) || !IsAgencyEnabled) {
            return <KText p="8px 12px">No options found</KText>;
          }
        }
        return <KText p="8px 12px">please type 2 or more letters</KText>;
      },
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[status][]=active&filter_value=`,
        processResponse: (res) =>
          res?.data?.users?.map((item) => ({
            ...item,
            label: `${item?.first_name} ${item?.last_name}`,
            value: item?.id,
            avatar: item?.avatar_thumb_url,
            ...item,
          })),
        httpType: 'v1',
      },
      styleVersion: 'v2',
    },
    {
      id: 'SCHEDULING OPTIONS',
      type: 'wrapper',
      Wrapper: CollapseSection,
      wrapperProps: {
        title: 'SCHEDULING OPTIONS',
        isHeaderClickable: true,
        defaultIsOpen: true,
        ...collapseSectionProps(),
      },
      fields: [
        {
          type: 'custom',
          Field: () => {
            return (
              <KTabBar
                options={options}
                selectedId={selectedTab}
                onChange={(value) => {
                  setSelectedTab(value);
                }}
              />
            );
          },
        },
        {
          type: 'custom',
          Field: () => (
            <Box py="16px">
              <ScheduleSettingsTabs
                selectedTab={selectedTab}
                formRef={formRef}
                modalContainerRef={modalContainerRef}
                selectedReport={selectedReport}
              />
            </Box>
          ),
        },
      ],
    },
  ];

  const handleFormSubmit = (formValues) => {
    const { name, description, frequency, recipient_ids } = formValues;

    const formPayload = { ...formValues };
    if (frequency === 'weekly') {
      delete formPayload.deliver_day_of_month;
    } else if (frequency === 'monthly') {
      delete formPayload.deliver_day_of_week;
    } else if (frequency === 'daily') {
      delete formPayload.deliver_day_of_week;
      delete formPayload.deliver_day_of_month;
    }

    // Separate external email recipients from internal user IDs
    const externalRecipientEmails = [];
    const internalRecipientIds = [];

    if (recipient_ids && Array.isArray(recipient_ids)) {
      recipient_ids.forEach((recipient) => {
        if (typeof recipient === 'string' && recipient.includes('@')) {
          // External email recipient
          externalRecipientEmails.push(recipient);
        } else {
          // Internal user ID
          internalRecipientIds.push(recipient);
        }
      });
    }

    // Update formPayload with separated recipients
    formPayload.recipient_ids = internalRecipientIds;
    if (externalRecipientEmails.length > 0) {
      formPayload.external_recipient_emails = externalRecipientEmails;
    }

    const subjectType = isCustomReport ? 'custom' : subject_type;
    const payload = {
      ...formPayload,
      subject_type: subjectType,
      query: {
        filter_by: {
          ...(subjectType !== 'live_job_pipeline' && {
            date: {
              from: timeFrame?.from,
            },
          }),
          ...filters,
        },
        groups: {
          //when custom reports come, need to use incremental id's
          [`${subject_type}_table_1`]: group,
        },
        ...(subjectType !== 'live_job_pipeline' && {
          date_group: timeFrame?.date_group,
        }),
      },
      ...(isCustomReport && { custom_report_id: currentData?.id }),
      ...(!isEditMode
        ? {
            metadata: {
              filtersToRender: selected,
              timeFrame,
              filters,
              ...(subject_type === 'custom' && {
                name,
                description,
              }),
              ...(passThroughMetric ? { passThroughMetric } : {}),
            },
          }
        : {}),
    };

    const apiAction = isEditMode ? updateReport : createReport;

    apiAction({ payload, id: selectedReport?.id }).then((response) => {
      if ('error' in response) {
        const error = response?.error || {};
        const message = error?.[0];

        toast({
          title: isEditMode
            ? 'Unable to update the email schedule'
            : 'Unable to create the email schedule',

          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: isEditMode
            ? 'Email schedule updated successfully'
            : 'Email scheduled successfully',
        });
        setCurrentView('list');
        // modalActions?.onClose();
      }
    });
  };

  const renderForm = () => {
    return (
      <Box p="0 24px 12px 24px" h="100%" overflowY="auto">
        <FormBuilder
          formRef={formRef}
          config={formConfig}
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
        />
      </Box>
    );
  };

  const handleDrawerSubmit = () => {
    if (formRef?.current) {
      formRef.current.submitForm();
    }
  };

  return (
    <>
      {modalActions?.isOpen && (
        <Drawer
          drawerRef={modalContainerRef}
          drawerActions={{
            ...modalActions,
            onClose: () => {
              if (isEmptySchedules) {
                modalActions.onClose();
              } else {
                setCurrentView('list');
              }
            },
          }}
          onDrawerSubmit={handleDrawerSubmit}
          headerText={isEditMode ? 'Edit email schedule' : 'Schedule email'}
          headerDescription="Schedule your visualizations to be emailed exactly when you need them"
          isCtaLoading={isCreating || isUpdating}
          ctaLabel={isEditMode ? 'Save' : 'Schedule email'}
        >
          <>
            {renderForm()}
            {/*
						todo: enable if api call needed
						{isFetching ? <CenterSpinner h="50vh" /> : renderForm()
						 */}
          </>
        </Drawer>
      )}
    </>
  );
};
